/**
 * Centralizes the released V4 scan-mode policy and its two allowed choices.
 * V3 keeps its existing UI while every submitted mode is normalized so the
 * unsupported historical Extensive value can never reach the backend.
 */

import { CANVAS_URL_PARAMS, isV3Pipeline } from "./canvasUrlParams";

export enum ScanMode {
  IMPATIENT = "impatient",
  BALANCED = "balanced",
}

const SCAN_MODE_STORAGE_PREFIX = "jml_scan_mode:";

export const isV4ScanModeEnabled = (
  search: string = window.location.search,
): boolean => !isV3Pipeline(search);

export const parseScanMode = (value: unknown): ScanMode | null => {
  if (value === ScanMode.IMPATIENT || value === ScanMode.BALANCED) {
    return value;
  }
  return null;
};

export const getScanMode = (projectId?: string | null): ScanMode => {
  if (!projectId) {
    return ScanMode.IMPATIENT;
  }
  return (
    parseScanMode(
      localStorage.getItem(`${SCAN_MODE_STORAGE_PREFIX}${projectId}`),
    ) ?? ScanMode.IMPATIENT
  );
};

export const storeScanMode = (
  projectId: string,
  scanMode: ScanMode,
): void => {
  localStorage.setItem(`${SCAN_MODE_STORAGE_PREFIX}${projectId}`, scanMode);
};

export const getInitialScanMode = (
  projectId?: string | null,
  search: string = window.location.search,
): ScanMode => {
  const preselected = parseScanMode(
    new URLSearchParams(search).get(CANVAS_URL_PARAMS.scanMode),
  );
  return preselected ?? getScanMode(projectId);
};

export const addScanModeToSocketPayload = (
  event: string,
  payload: unknown,
): unknown => {
  if (
    event !== "code_connect" ||
    typeof payload !== "object" ||
    payload === null
  ) {
    return payload;
  }
  const record = payload as Record<string, unknown>;
  if (parseScanMode(record.scan_mode)) return payload;
  const projectId =
    typeof record.project_id === "string" ? record.project_id : undefined;
  return {
    ...record,
    scan_mode: getScanMode(projectId),
  };
};
